// src/pages/payments/PaymentSuccess.jsx
// FASE 3.A — PG-04 Pago confirmado (pixel-perfect del mockup v4).
// FASE 3.B — Plan/monto/vence se leen desde userData (live snapshot de
// /users/{uid}). flowConfirm setea lastPaymentPlan, lastPaymentAmount,
// accessUntil tras procesar el pago en Flow. paypalWebhook lo mismo +
// lastPaymentCurrency='USD' (multi-currency support, 2026-06-10).
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../auth/AuthContext';

const PLAN_LABELS = {
  monthly: 'Mensual',
  monthly_intl: 'Mensual',  // PayPal USD 25 — mismo label visual que monthly
  quarterly: 'Trimestral',
  biannual: 'Semestral',
};

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const formatExpDate = (timestamp) => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const day = date.getDate();
  const month = MONTHS_ES[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Multi-currency: lee lastPaymentCurrency del doc. Default CLP para back-compat
// con docs viejos de Flow que no tenían ese campo. USD se formatea como
// "US$25" (es-CL no aplica símbolo de peso para foreign currency).
const formatAmount = (amount, currency) => {
  if (!amount) return '—';
  const ccy = (currency || 'CLP').toUpperCase();
  if (ccy === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const planLabel = userData?.lastPaymentPlan
    ? PLAN_LABELS[userData.lastPaymentPlan] || userData.lastPaymentPlan
    : '—';
  const amountStr = formatAmount(userData?.lastPaymentAmount, userData?.lastPaymentCurrency);
  const expDateStr = formatExpDate(userData?.accessUntil);

  return (
    <div className="mobile-root">
      <Helmet>
        <title>¡Listo! · Edge Elite</title>
      </Helmet>

      <section
        className="screen"
        style={{
          paddingTop: 60,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          paddingBottom: 120,
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 999,
            background: 'rgba(22,163,74,.14)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 28,
          }}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--v-enter)" strokeWidth="2.6">
            <path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            margin: 0,
            marginBottom: 8,
          }}
        >
          ¡Bienvenido a Edge Elite! 🎯
        </h2>
        <p style={{ fontSize: 15, color: 'var(--muted)', margin: 0, marginBottom: 32, maxWidth: 320 }}>
          Tu pago se confirmó y tu acceso ya está activo.
        </p>

        <div
          style={{
            width: '100%',
            maxWidth: 320,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: 16,
            marginBottom: 24,
            textAlign: 'left',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Plan</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{planLabel}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Pagado</span>
            <span style={{ fontSize: 13, fontWeight: 600 }} className="num">
              {amountStr}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Acceso hasta</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{expDateStr}</span>
          </div>
        </div>

        {/* Tu acceso incluye — 3 pilares */}
        <div style={{ width: '100%', maxWidth: 320, textAlign: 'left' }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Tu acceso incluye</div>
          {[
            { icon: '📊', t: 'Dashboard de análisis en vivo', d: 'Proyección vs línea, edge y veredicto en tiempo real.' },
            { icon: '🔔', t: 'Alertas en vivo por Telegram', d: 'El análisis llega a tu chat cuando el motor detecta valor.' },
            { icon: '👥', t: 'Comunidad VIP', d: 'Comenta las apuestas y aprende con el resto del grupo.' },
          ].map((it, i, arr) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: i < arr.length - 1 ? 14 : 0 }}>
              <div style={{ fontSize: 20, flexShrink: 0, lineHeight: 1 }}>{it.icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{it.t}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.45 }}>{it.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="sticky-cta">
        <button className="btn-primary accent" onClick={() => navigate('/conectar-telegram')}>
          Conectar Telegram ahora
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
